import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ZEITLIMIT_MS, mitZeitlimit } from './zeitlimit';

// Module-level cache. Persists across mounts so navigating away and back
// to a page renders the previous result instantly and silently refetches
// in the background. Keyed by an opaque string — pages compose their own
// key from filter state so distinct filters cache independently.

type CacheEntry = { data: unknown; ts: number };
const cache = new Map<string, CacheEntry>();
// Laufende Anfragen, mit Startzeitpunkt — der Zeitstempel wird gebraucht, um
// eine hängen gebliebene Anfrage von einer normal laufenden zu unterscheiden
// (s. revalidateMounted).
const inflight = new Map<string, { promise: Promise<unknown>; startedAt: number }>();

// Aktuell gemountete Hooks, die auf „vielleicht stale, bitte prüfen"
// reagieren sollen — s. revalidateMounted() unten.
interface StaleListener { key: string; bump: () => void }
const staleListeners = new Set<StaleListener>();

// Stößt bei allen gemounteten Hooks eine Prüfung an, ob ihr Cache-Eintrag
// abgelaufen ist; nur die tatsächlich abgelaufenen laden neu (die
// Freshness-Prüfung sitzt im Fetch-Effect unten, hier wird nur „nachgeschaut").
// Bewusst NICHT an DB-Events/Realtime gekoppelt: ein früherer Versuch, bei
// jeder call_log-Änderung zu invalidieren, hat während des Altsystem-Abgleichs
// (viele Schreibvorgänge) einen Refetch-Sturm ausgelöst, der alle Listen lahmgelegt
// hat (siehe CHANGELOG 2026-06-30, „Incident — Refetch-Sturm"). Hier läuft es nur
// bei echten Nutzer-Signalen (Tab-Fokus) und einem groben, gedrosselten Timer —
// pro Query höchstens ein Request alle staleMs, dedupliziert über `inflight`.
function revalidateMounted(): void {
  // Während eine Anfrage noch läuft NICHT nachtreten. Auf einer wackeligen
  // Leitung hat der Hintergrund-Timer sonst genau dann eine zweite Anfrage
  // gestartet, wenn die erste schon hing — die Seite stand danach doppelt so
  // lange auf „Lade…".
  //
  // ABER: nur, solange die laufende Anfrage plausibel noch unterwegs ist.
  // Vorher genügte EINE hängende Anfrage, um das Nachladen ALLER Seiten für
  // den Rest der Sitzung stillzulegen — genau das Bild „ich navigiere auf
  // Kunden und der Inhalt kommt nicht, erst ein Browser-Refresh hilft".
  if (hatFrischeAnfrage()) return;
  for (const l of staleListeners) l.bump();
}

function hatFrischeAnfrage(): boolean {
  const jetzt = Date.now();
  for (const [, v] of inflight) {
    if (jetzt - v.startedAt < STUCK_AFTER_MS) return true;
  }
  return false;
}

if (typeof window !== 'undefined') {
  window.addEventListener('focus', revalidateMounted);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') revalidateMounted();
  });
  // Drosselung: alle 45s nachschauen statt einer eigenen Schleife pro Seite —
  // nur während der Tab sichtbar ist (im Hintergrund gibt es nichts zu sehen).
  setInterval(() => {
    if (document.visibilityState === 'visible') revalidateMounted();
  }, 45_000);
}

// Wiederholungen bei transienten Fehlern. Bewusst knapp gehalten: jeder Versuch
// kann bis zum Zeitlimit des Clients dauern (s. supabase.ts), und der Nutzer
// sieht währenddessen nur „Lade…". Frühzeitig mit Fehler + „Erneut laden"
// abzubrechen ist besser als minutenlanges Warten.
const MAX_ATTEMPTS = 2;
const RETRY_BACKOFF_MS = 600;

// Ab wann eine laufende Anfrage als „hängt" gilt und die Oberfläche das sagen
// darf, statt weiter stumm „Lade…" zu zeigen.
export const SLOW_AFTER_MS = 6_000;

// Notbremse um den GESAMTEN Ladevorgang — Begründung und Wert siehe
// lib/zeitlimit.ts. Dasselbe Limit schützt inzwischen auch das Speichern.
// Ab wann eine noch nicht abgeschlossene Anfrage als hängengeblieben gilt und
// die Hintergrund-Aktualisierung nicht mehr blockieren darf.
const STUCK_AFTER_MS = ZEITLIMIT_MS + 4_000;

// ------------------------------------------------- „hängt gerade"-Signal ----
// Damit die Oberfläche EINMAL zentral sagen kann, dass gerade etwas klemmt
// (statt dass jede Liste es für sich merkt): jeder Hook meldet sich an und ab,
// die Layout-Leiste hört zu. Siehe components/LoadingHint.
const slowKeys = new Set<string>();
const slowSubscribers = new Set<() => void>();

function setSlow(key: string, slow: boolean): void {
  const vorher = slowKeys.size;
  if (slow) slowKeys.add(key); else slowKeys.delete(key);
  if (slowKeys.size !== vorher) slowSubscribers.forEach((f) => f());
}

/** true, sobald irgendeine gemountete Abfrage ungewöhnlich lange braucht. */
export function useAnyQueryStuck(): boolean {
  return useSyncExternalStore(
    (cb) => { slowSubscribers.add(cb); return () => { slowSubscribers.delete(cb); }; },
    () => slowKeys.size > 0,
    () => false,
  );
}

export interface CachedQueryState<T> {
  data: T | undefined;
  isFetching: boolean;
  /** Läuft ungewöhnlich lange (> SLOW_AFTER_MS) — Oberfläche soll das zeigen. */
  isSlow: boolean;
  error: string | null;
  refresh: () => void;
}

export function useCachedQuery<T>(
  key: string | null,
  fetcher: () => Promise<T>,
  options: { staleMs?: number } = {},
): CachedQueryState<T> {
  const { staleMs = 30_000 } = options;
  // setTick after the background fetch lands triggers a re-render; the
  // returned `data` then reads the fresh value out of the module cache.
  // `tick` steckt bewusst in den Effect-Deps: refresh() löscht den Cache und
  // bumpt tick → der Effect läuft erneut und lädt WIRKLICH nach (vorher wurde
  // nach z. B. „Erledigt" nie nachgeladen und die Liste blieb bis zum
  // Seiten-Reload leer). Kein Endlos-Loop: nach erfolgreichem Laden ist der
  // Cache frisch und der Effect steigt sofort wieder aus.
  const [tick, setTick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isSlow, setIsSlow] = useState(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Bei Tab-Fokus/-Sichtbarkeit und dem gedrosselten Timer (s. oben) meldet
  // sich dieser Hook zur Prüfung an — bumpt nur `tick`, die eigentliche
  // Freshness-Prüfung (unten) entscheidet, ob wirklich neu geladen wird.
  useEffect(() => {
    if (!key) return;
    const listener: StaleListener = { key, bump: () => setTick((t) => t + 1) };
    staleListeners.add(listener);
    return () => { staleListeners.delete(listener); };
  }, [key]);

  // Beim Verlassen der Seite darf dieser Hook das globale „hängt"-Signal nicht
  // stehen lassen.
  useEffect(() => {
    if (!key) return;
    return () => { setSlow(key, false); };
  }, [key]);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const cached = cache.get(key);
    const fresh = cached && Date.now() - cached.ts < staleMs;
    if (fresh) {
      // Ein früherer Durchlauf kann isFetching gesetzt und wegen des
      // Abbruchs nie zurückgesetzt haben — dann stand „aktualisiere…"
      // dauerhaft in der Überschrift, obwohl längst nichts mehr lief.
      setIsFetching(false);
      setIsSlow(false);
      setSlow(key, false);
      return;
    }
    setIsFetching(true);
    setIsSlow(false);
    // Nach SLOW_AFTER_MS darf die Seite sagen, dass es hängt (statt weiter
    // stumm „Lade…" anzuzeigen) — mit „Erneut laden" als Ausweg.
    const slowTimer = window.setTimeout(() => {
      if (cancelled) return;
      setIsSlow(true);
      setSlow(key, true);
    }, SLOW_AFTER_MS);
    const laufend = inflight.get(key);
    // Der eigene Eintrag, damit das Aufräumen am Ende NUR ihn entfernt. Ohne
    // diesen Vergleich hat eine spät eintreffende alte Anfrage den Eintrag der
    // frisch gestarteten mitgelöscht (passiert nach „Erneut laden", das die
    // hängende Anfrage bewusst verwirft) — danach lief die neue Anfrage
    // unbemerkt und ein zweiter Aufrufer hätte dieselbe Abfrage doppelt gestellt.
    let eintrag: { promise: Promise<unknown>; startedAt: number } | null = null;
    const run = laufend?.promise ?? (async () => {
      try {
        // Ein Neuversuch bei einem transienten Fehler (kurzer Netzwerk-Hänger)
        // — sonst blieb eine Seite bis zum manuellen Reload auf dem
        // Fehlzustand stehen. Bewusst NUR ein pro-Request-Retry mit Backoff,
        // kein push-getriggerter Refetch — kann keinen Sturm auslösen.
        let lastErr: unknown;
        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
          try {
            const data = await mitZeitlimit(fetcherRef.current());
            cache.set(key, { data, ts: Date.now() });
            return data;
          } catch (e) {
            lastErr = e;
            if (attempt < MAX_ATTEMPTS - 1) {
              await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS * (attempt + 1)));
            }
          }
        }
        throw lastErr;
      } finally {
        if (eintrag && inflight.get(key) === eintrag) inflight.delete(key);
      }
    })();
    if (!laufend) {
      eintrag = { promise: run, startedAt: Date.now() };
      inflight.set(key, eintrag);
    }
    run.then(
      () => {
        if (cancelled) return;
        setError(null);
        setIsFetching(false);
        setIsSlow(false);
        setSlow(key, false);
        setTick((t) => t + 1);
      },
      (err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setIsFetching(false);
        setIsSlow(false);
        setSlow(key, false);
      },
    );
    return () => { cancelled = true; window.clearTimeout(slowTimer); };
  }, [key, staleMs, tick]);

  const refresh = () => {
    if (!key) return;
    cache.delete(key);
    // Eine hängende Anfrage darf den Neuversuch nicht schlucken: sonst hängt
    // sich „Erneut laden" an genau die Anfrage, die gerade nicht antwortet,
    // und der Knopf tut scheinbar nichts.
    inflight.delete(key);
    setError(null);
    setTick((t) => t + 1);
  };

  return {
    data: key ? (cache.get(key)?.data as T | undefined) : undefined,
    isFetching,
    isSlow,
    error,
    refresh,
  };
}

export function invalidateCache(predicate?: (key: string) => boolean): void {
  if (!predicate) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (predicate(key)) cache.delete(key);
  }
}
